// Videorender query + mutation hooks.
//
// Mirrors useCharacters.ts: rendering is ASYNC, so useVideos auto-polls (every
// 3s) while any row is still queued|generating, then stops — the gallery flips
// to the playable video without a manual refresh.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { RenderStoryInput, Video } from '@/api/types'

/** Stable cache key for the recent-videos list. */
export const videosKey = ['videos'] as const

/**
 * useVideos — fetch the 50 most-recent videos (updated_at DESC). Polls every 3s
 * while any render is still queued or generating, then idles.
 */
export function useVideos() {
  return useQuery<Video[]>({
    queryKey: videosKey,
    queryFn: api.listVideos,
    refetchInterval: (query) =>
      query.state.data?.some((v) => v.status === 'queued' || v.status === 'generating')
        ? 3000
        : false,
  })
}

/**
 * useRenderStory — POST a render request for a story. The returned `generating`
 * row triggers a list invalidate so the new card (and its polling) appears
 * immediately.
 */
export function useRenderStory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { storyId: number; input: RenderStoryInput }) =>
      api.renderStory(vars.storyId, vars.input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: videosKey })
    },
  })
}

/**
 * useRetryVideo — re-render a video in place (async). Invalidates the list so the
 * row flips back to `generating` and the gallery resumes polling.
 */
export function useRetryVideo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.retryVideo(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: videosKey })
    },
  })
}

/** useDeleteVideo — remove a video, then drop it from the list. */
export function useDeleteVideo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.deleteVideo(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: videosKey })
    },
  })
}
